import React, { useEffect, useState } from "react";
import { api, fmtINR, fmtDate, formatErr, today } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PageHeader, Card, Empty } from "@/components/ui-bits";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Trash2, Share2, User, Store, Phone, Send, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const MODES = ["Cash", "UPI", "Bank Transfer", "Cheque"];

export default function Payments() {
  const { user } = useAuth();
  const [list, setList] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    customer_id: "", amount: "", payment_mode: "Cash",
    reference_no: "", payment_date: today(), remarks: "",
  });

  // WhatsApp Payment Receipt Share Modal state
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [sharePayment, setSharePayment] = useState(null);
  const [shareCustomer, setShareCustomer] = useState(null);

  function load() {
    api.get("/payments").then(r => setList(r.data));
    if (user?.role === "admin") api.get("/customers").then(r => setCustomers(r.data));
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  function buildPaymentShareText(p, cust) {
    const lines = [
      `*Omkar Milk Distributor*`,
      `💳 *PAYMENT RECEIPT ACKNOWLEDGMENT*`,
      `----------------------------------`,
      `Receipt Date: ${fmtDate(p.payment_date)}`,
      `Received From: *${cust?.business_name || p.customer_name}*`,
      `Contact Person: ${cust?.customer_name || p.customer_name}`,
      "",
      `*Amount Received: ₹${Number(p.amount).toFixed(2)}*`,
      `Payment Mode: *${p.payment_mode}*`,
    ];
    if (p.reference_no) lines.push(`Reference / Ref No: ${p.reference_no}`);
    if (p.remarks) lines.push(`Notes: ${p.remarks}`);
    
    if (cust) {
      lines.push("");
      if (cust.advance > 0) {
        lines.push(`*Current Account Status: Advance Balance ₹${Number(cust.advance).toFixed(2)}*`);
      } else {
        lines.push(`*Current Account Status: Remaining Dues ₹${Number(cust.outstanding || 0).toFixed(2)}*`);
      }
    }
    lines.push("");
    lines.push("Thank you for your payment!");
    return lines.join("\n");
  }

  function handleSharePayment(p) {
    const cust = customers.find(c => c.id === p.customer_id) || { customer_name: p.customer_name, business_name: p.customer_name, mobile: p.customer_mobile || "" };
    if (cust && cust.mobile_secondary) {
      setSharePayment(p);
      setShareCustomer(cust);
      setShareModalOpen(true);
    } else {
      executeSharePayment(p, cust, cust?.mobile || "");
    }
  }

  function executeSharePayment(p, cust, phoneInput) {
    const phoneRaw = (phoneInput || "").replace(/\D/g, "");
    const phone = phoneRaw.length === 10 ? `91${phoneRaw}` : phoneRaw;
    const text = encodeURIComponent(buildPaymentShareText(p, cust));
    const url = phone ? `https://wa.me/${phone}?text=${text}` : `https://wa.me/?text=${text}`;
    window.open(url, "_blank", "noopener,noreferrer");
    setShareModalOpen(false);
  }

  async function save() {
    if (!form.customer_id) return toast.error("Pick a customer");
    if (!Number(form.amount)) return toast.error("Enter amount");
    try {
      const res = await api.post("/payments", { ...form, amount: Number(form.amount) });
      toast.success("Payment recorded");
      setOpen(false);
      
      const newPayment = res.data;
      const cust = customers.find(c => c.id === form.customer_id);
      
      setForm({ customer_id: "", amount: "", payment_mode: "Cash", reference_no: "", payment_date: today(), remarks: "" });
      load();

      // Automatically offer WhatsApp receipt sharing after recording payment
      if (cust) {
        if (cust.mobile_secondary) {
          setSharePayment(newPayment);
          setShareCustomer(cust);
          setShareModalOpen(true);
        } else {
          executeSharePayment(newPayment, cust, cust.mobile || "");
        }
      }
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  }
  async function remove(p) {
    if (!window.confirm("Reverse this payment?")) return;
    await api.delete(`/payments/${p.id}`); toast.success("Removed"); load();
  }

  return (
    <div data-testid="payments-page">
      <PageHeader
        eyebrow="Receivables"
        title="Payments"
        description={user?.role === "customer" ? "All payments recorded on your account." : "Record cash, UPI, bank, or cheque receipts."}
        actions={user?.role === "admin" && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="bg-cobalt-500 hover:bg-cobalt-700 text-white" data-testid="payment-create-btn"><Plus size={16} className="mr-1"/>Record Payment</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Record Payment</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label>Customer</Label>
                  <Select value={form.customer_id} onValueChange={(v)=>setForm({...form, customer_id: v})}>
                    <SelectTrigger data-testid="payment-customer"><SelectValue placeholder="Select customer"/></SelectTrigger>
                    <SelectContent>{customers.map(c => <SelectItem key={c.id} value={c.id}>{c.business_name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Amount (₹)</Label>
                  <Input type="number" data-testid="payment-amount" value={form.amount} onChange={e=>setForm({...form, amount: e.target.value})}/>
                </div>
                <div>
                  <Label>Mode</Label>
                  <Select value={form.payment_mode} onValueChange={(v)=>setForm({...form, payment_mode: v})}>
                    <SelectTrigger><SelectValue/></SelectTrigger>
                    <SelectContent>{MODES.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Date</Label>
                  <Input type="date" value={form.payment_date} onChange={e=>setForm({...form, payment_date: e.target.value})}/>
                </div>
                <div>
                  <Label>Reference No</Label>
                  <Input value={form.reference_no} onChange={e=>setForm({...form, reference_no: e.target.value})} placeholder="UPI ref / Cheque no"/>
                </div>
                <div className="col-span-2">
                  <Label>Remarks</Label>
                  <Textarea rows={2} value={form.remarks} onChange={e=>setForm({...form, remarks: e.target.value})}/>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={()=>setOpen(false)}>Cancel</Button>
                <Button onClick={save} className="bg-cobalt-500 hover:bg-cobalt-700" data-testid="payment-save-btn">Save</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      />

      <Card padding="p-0">
        {list.length === 0 ? <div className="p-8"><Empty title="No payments recorded yet"/></div> : (
          <div className="overflow-x-auto">
            <table className="ddms-table" data-testid="payments-table">
              <thead>
                <tr><th>Date</th><th>Customer</th><th>Mode</th><th>Reference</th><th className="num">Amount</th><th>Remarks</th><th className="num">Actions</th></tr>
              </thead>
              <tbody>
                {list.map(p => (
                  <tr key={p.id}>
                    <td className="font-mono text-xs">{fmtDate(p.payment_date)}</td>
                    <td className="font-medium">{p.customer_name}</td>
                    <td><span className="px-2 py-0.5 rounded-full text-[11px] bg-emerald-50 text-emerald-700 font-medium">{p.payment_mode}</span></td>
                    <td className="font-mono text-xs">{p.reference_no || "—"}</td>
                    <td className="num font-semibold text-emerald-600">{fmtINR(p.amount)}</td>
                    <td className="text-slate-500">{p.remarks}</td>
                    <td className="num flex justify-end gap-1.5">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleSharePayment(p)}
                        className="border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                        title="Share Receipt on WhatsApp"
                        data-testid={`share-payment-${p.id}`}
                      >
                        <Share2 size={12} className="mr-1" /> Receipt
                      </Button>
                      {user?.role==="admin" && (
                        <Button size="sm" variant="outline" onClick={()=>remove(p)} className="text-rose-600 border-rose-200 hover:bg-rose-50"><Trash2 size={12}/></Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* WhatsApp Payment Receipt Modal */}
      <Dialog open={shareModalOpen} onOpenChange={setShareModalOpen}>
        <DialogContent className="sm:max-w-md p-0 overflow-hidden border-0 shadow-2xl rounded-2xl">
          <div className="bg-gradient-to-r from-emerald-600 to-teal-700 p-5 text-white">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-white text-lg font-heading font-semibold">
                <div className="p-2 bg-white/10 backdrop-blur-md rounded-xl">
                  <Share2 size={20} className="text-white" />
                </div>
                Share Payment Receipt on WhatsApp
              </DialogTitle>
            </DialogHeader>
            <div className="mt-3 flex items-center justify-between text-xs text-emerald-100 bg-white/10 backdrop-blur-md rounded-lg px-3 py-2 border border-white/15">
              <div>
                <span className="opacity-75">Date:</span> <strong className="text-white font-mono text-xs">{fmtDate(sharePayment?.payment_date)}</strong>
              </div>
              <div className="text-right">
                <span className="opacity-75">Received:</span> <strong className="text-white font-mono text-sm">₹{Number(sharePayment?.amount || 0).toFixed(2)} ({sharePayment?.payment_mode})</strong>
              </div>
            </div>
          </div>

          <div className="p-5 bg-slate-50/50">
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3 flex items-center gap-1.5">
              <Store size={14} className="text-cobalt-600" />
              <span>{shareCustomer?.business_name || sharePayment?.customer_name}</span>
              <span className="text-slate-400 font-normal">({shareCustomer?.customer_name || sharePayment?.customer_name})</span>
            </div>

            <div className="space-y-3">
              <div
                onClick={() => executeSharePayment(sharePayment, shareCustomer, shareCustomer?.mobile)}
                className="group relative flex items-center justify-between p-4 bg-white hover:bg-emerald-50/60 border-2 border-emerald-500/30 hover:border-emerald-500 rounded-xl cursor-pointer transition-all duration-200 shadow-sm hover:shadow-md"
              >
                <div className="flex items-center gap-3.5">
                  <div className="w-10 h-10 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center font-bold transition-transform group-hover:scale-105">
                    <User size={18} />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-slate-900 text-sm">Owner (Primary)</span>
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-emerald-100 text-emerald-800">Primary</span>
                    </div>
                    <div className="text-xs font-mono text-slate-600 mt-0.5 flex items-center gap-1">
                      <Phone size={12} className="text-slate-400" />
                      {shareCustomer?.mobile || "N/A"}
                    </div>
                  </div>
                </div>
                <div className="w-8 h-8 rounded-full bg-emerald-500 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-200 transform group-hover:translate-x-0 -translate-x-2">
                  <Send size={14} />
                </div>
              </div>

              {shareCustomer?.mobile_secondary && (
                <div
                  onClick={() => executeSharePayment(sharePayment, shareCustomer, shareCustomer?.mobile_secondary)}
                  className="group relative flex items-center justify-between p-4 bg-white hover:bg-cobalt-50/60 border-2 border-slate-200 hover:border-cobalt-400 rounded-xl cursor-pointer transition-all duration-200 shadow-sm hover:shadow-md"
                >
                  <div className="flex items-center gap-3.5">
                    <div className="w-10 h-10 rounded-full bg-cobalt-100 text-cobalt-700 flex items-center justify-center font-bold transition-transform group-hover:scale-105">
                      <Store size={18} />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-slate-900 text-sm">Cashier / Shop Manager</span>
                        <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-slate-100 text-slate-700">Secondary</span>
                      </div>
                      <div className="text-xs font-mono text-slate-600 mt-0.5 flex items-center gap-1">
                        <Phone size={12} className="text-slate-400" />
                        {shareCustomer?.mobile_secondary}
                      </div>
                    </div>
                  </div>
                  <div className="w-8 h-8 rounded-full bg-cobalt-600 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-200 transform group-hover:translate-x-0 -translate-x-2">
                    <Send size={14} />
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="px-5 py-3.5 bg-slate-100 border-t border-slate-200 flex justify-end">
            <Button variant="outline" size="sm" onClick={() => setShareModalOpen(false)} className="rounded-lg text-xs font-medium">
              Cancel
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
